import { Page, type CapsuleTitle } from '../ui';
import { modelledEarningsPerSale } from '@goodbuild/rules/creator-earnings';
import { formatMoney } from '@goodbuild/rules/format';
import { MINIMUM_CREATOR_RATE_BPS, PLATFORM_RATE_BPS } from '@goodbuild/rules/ledger';
import { CreatorsPitch } from '../components/creators-pitch';
import { useShelf } from '../catalogue';
import { useDocumentTitle } from '../title';

/**
 * The creators pitch.
 *
 * `site="creators"` gives it this audience's chrome and ground colour: section
 * 04 puts one channel per surface, so a reader knows where they are before
 * reading a word. It is a page on the store's origin wearing the creators'
 * everything, which is the same arrangement it had as an Astro route.
 *
 * **The shelf is computed here rather than in the component**, which is
 * unchanged from the Astro version and for the same reason: a React component
 * cannot be async. What changed is that the awaiting is a query rather than
 * frontmatter, so the arithmetic runs when the catalogue lands instead of
 * before the page is sent.
 */
export function CreatorsRoute(): React.ReactElement {
  useDocumentTitle('For creators');

  const query = useShelf(12);

  const shelf: CapsuleTitle[] = (query.data?.entries ?? []).flatMap((entry) => {
    const money = entry.prices.find((price) => price.currency === 'EUR') ?? entry.prices[0];
    if (!money) return [];

    /*
     * The floor commission on one sale.
     *
     * Through `modelledEarningsPerSale` rather than a percentage of the sticker
     * price, so this is the same arithmetic that will later write the ledger
     * entry: commission is paid on net, after VAT is extracted and processing
     * is taken off. A percentage of list price would overstate it by roughly a
     * quarter, which is the "effective rate, not headline rate" problem section
     * 3.3 exists to prevent.
     *
     * The platform tier is passed because the engine takes it, and it does not
     * move this number: the creator's commission comes off net before the
     * platform's share is computed at all.
     */
    const floor = modelledEarningsPerSale(
      money.currency,
      money.amountMinor,
      MINIMUM_CREATOR_RATE_BPS,
      PLATFORM_RATE_BPS,
    );
    if (floor <= 0) return [];

    return [
      {
        slug: entry.listing.slug,
        name: entry.listing.name,
        studio: entry.listing.developerName,
        price: `${formatMoney(money.currency, floor)}+`,
        priceLabel: 'You earn per sale',
        href: `/g/${entry.listing.slug}`,
        artUrl: null,
      },
    ];
  });

  /*
   * The pitch draws whether or not the shelf has landed.
   *
   * `CreatorsPitch` renders nothing for an empty shelf — a grid with no
   * capsules in it is simply absent — so an unfinished request costs the games
   * section and none of the argument around it. Holding the whole page back for
   * a rail would be trading the thing a creator came to read for the thing they
   * scroll past.
   */
  return (
    <Page site="creators">
      <CreatorsPitch shelf={shelf} />
    </Page>
  );
}
